import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Group,
  Person,
  PersonAdd,
  PersonRemove,
  ExpandMore,
  ExpandLess,
  AdminPanelSettings,
  SupervisorAccount,
  AccountTree,
  Healing,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { apiRequest } from '../../utils/api';
import { MobileBottomNav } from '../common/MobileBottomNav';

export default function AccessGroupsAdmin() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialogs
  const [createDialog, setCreateDialog] = useState({ open: false, parentId: null });
  const [editDialog, setEditDialog] = useState({ open: false, group: null });
  const [addMemberDialog, setAddMemberDialog] = useState({ open: false, groupId: null });
  const [addPatientDialog, setAddPatientDialog] = useState({ open: false, groupId: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, groupId: null, name: '' });

  // Form state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [memberRole, setMemberRole] = useState('member');

  // Expanded groups
  const [expanded, setExpanded] = useState({});

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/access-groups/');
      setGroups(data.groups || []);
    } catch (e) {
      setError('Error al cargar grupos');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await apiRequest('/users/');
      setAllUsers(data || []);
    } catch (e) {
      setAllUsers([]);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchUsers();
  }, [fetchGroups, fetchUsers]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // --- CRUD ---
  const handleCreateGroup = async () => {
    try {
      const body = { name: formName, description: formDesc };
      if (createDialog.parentId) body.parent_group_id = createDialog.parentId;
      await apiRequest('/access-groups/', { method: 'POST', body: JSON.stringify(body) });
      setCreateDialog({ open: false, parentId: null });
      setFormName(''); setFormDesc('');
      setSuccess('Grupo creado');
      fetchGroups();
    } catch (e) {
      setError('Error al crear grupo');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      await apiRequest(`/access-groups/${editDialog.group.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: formName, description: formDesc }),
      });
      setEditDialog({ open: false, group: null });
      setFormName(''); setFormDesc('');
      setSuccess('Grupo actualizado');
      fetchGroups();
    } catch (e) {
      setError('Error al actualizar grupo');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await apiRequest(`/access-groups/${deleteConfirm.groupId}`, { method: 'DELETE' });
      setDeleteConfirm({ open: false, groupId: null, name: '' });
      setSuccess('Grupo eliminado');
      fetchGroups();
    } catch (e) {
      setError('Error al eliminar grupo');
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await apiRequest(`/access-groups/${addMemberDialog.groupId}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, role_in_group: memberRole }),
      });
      setSuccess('Miembro agregado');
      fetchGroups();
    } catch (e) {
      setError('Error al agregar miembro');
    }
  };

  const handleRemoveMember = async (groupId, userId) => {
    try {
      await apiRequest(`/access-groups/${groupId}/members/${userId}`, { method: 'DELETE' });
      setSuccess('Miembro removido');
      fetchGroups();
    } catch (e) {
      setError('Error al remover miembro');
    }
  };

  const handleAddPatient = async (patientId) => {
    try {
      await apiRequest(`/access-groups/${addPatientDialog.groupId}/patients`, {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId }),
      });
      setSuccess('Paciente agregado');
      fetchGroups();
    } catch (e) {
      setError('Error al agregar paciente');
    }
  };

  const handleRemovePatient = async (groupId, patientId) => {
    try {
      await apiRequest(`/access-groups/${groupId}/patients/${patientId}`, { method: 'DELETE' });
      setSuccess('Paciente removido');
      fetchGroups();
    } catch (e) {
      setError('Error al remover paciente');
    }
  };

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  const employees = allUsers.filter(u => ['employee', 'admin'].includes(u.role));
  const patients = allUsers.filter(u => u.role === 'patient');

  const filteredEmployees = memberSearch
    ? employees.filter(u => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(memberSearch.toLowerCase()))
    : employees;

  const filteredPatients = patientSearch
    ? patients.filter(u => `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(patientSearch.toLowerCase()))
    : patients;

  // --- Render group tree recursively ---
  const renderGroup = (group, depth = 0) => {
    const isExpanded = expanded[group.id] !== false; // default open
    const memberCount = (group.members || []).length;
    const patientCount = (group.patients || []).length;
    const childCount = (group.children || []).length;

    return (
      <motion.div key={group.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
        <Paper
          elevation={0}
          sx={{
            ml: depth * 3,
            mb: 1,
            border: '1px solid',
            borderColor: depth === 0 ? 'primary.main' : 'divider',
            borderLeft: `4px solid ${depth === 0 ? '#2563EB' : '#94A3B8'}`,
            overflow: 'hidden',
          }}
        >
          {/* Group Header */}
          <Box
            sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' } }}
            onClick={() => toggleExpand(group.id)}
          >
            <AccountTree sx={{ color: depth === 0 ? 'primary.main' : 'text.secondary', fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600}>{group.name}</Typography>
              {group.description && (
                <Typography variant="caption" color="text.secondary">{group.description}</Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Chip icon={<Person />} label={memberCount} size="small" variant="outlined" />
              <Chip icon={<Healing />} label={patientCount} size="small" variant="outlined" color="success" />
              {childCount > 0 && <Chip icon={<AccountTree />} label={childCount} size="small" variant="outlined" color="info" />}
            </Stack>
            <Stack direction="row" spacing={0}>
              <Tooltip title="Agregar sub-grupo">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCreateDialog({ open: true, parentId: group.id }); setFormName(''); setFormDesc(''); }}>
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditDialog({ open: true, group }); setFormName(group.name); setFormDesc(group.description || ''); }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ open: true, groupId: group.id, name: group.name }); }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>

          {/* Expanded Content */}
          <Collapse in={isExpanded}>
            <Divider />
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#FAFBFC' }}>
              {/* Members */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  MIEMBROS ({memberCount})
                </Typography>
                <Button size="small" startIcon={<PersonAdd />} onClick={() => { setAddMemberDialog({ open: true, groupId: group.id }); setMemberSearch(''); setMemberRole('member'); }}>
                  Agregar
                </Button>
              </Stack>
              {(group.members || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>Sin miembros</Typography>
              ) : (
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                  {(group.members || []).map(m => (
                    <Box key={m.id || m.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: m.role_in_group === 'manager' ? '#7C3AED' : '#64748B' }}>
                        {m.role_in_group === 'manager' ? <SupervisorAccount sx={{ fontSize: 14 }} /> : <Person sx={{ fontSize: 14 }} />}
                      </Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {m.user_first_name || ''} {m.user_last_name || ''}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">({m.user_email || ''})</Typography>
                      </Typography>
                      <Chip label={m.role_in_group} size="small" color={m.role_in_group === 'manager' ? 'secondary' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
                      <IconButton size="small" onClick={() => handleRemoveMember(group.id, m.user_id)}>
                        <PersonRemove sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Patients */}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  PACIENTES ({patientCount})
                </Typography>
                <Button size="small" startIcon={<PersonAdd />} color="success" onClick={() => { setAddPatientDialog({ open: true, groupId: group.id }); setPatientSearch(''); }}>
                  Agregar
                </Button>
              </Stack>
              {(group.patients || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Sin pacientes</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {(group.patients || []).map(p => (
                    <Box key={p.id || p.patient_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#10B981' }}>
                        <Healing sx={{ fontSize: 14 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {p.patient_first_name || ''} {p.patient_last_name || ''}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">({p.patient_email || ''})</Typography>
                      </Typography>
                      <IconButton size="small" onClick={() => handleRemovePatient(group.id, p.patient_id)}>
                        <PersonRemove sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Paper>

        {/* Render children recursively */}
        {isExpanded && (group.children || []).map(child => renderGroup(child, depth + 1))}
      </motion.div>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 10, md: 4 } }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h2">Gestión de Grupos</Typography>
              <Typography variant="body2" color="text.secondary">
                Organizá empleados y pacientes en grupos jerárquicos
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => { setCreateDialog({ open: true, parentId: null }); setFormName(''); setFormDesc(''); }}
          >
            Nuevo Grupo
          </Button>
        </Stack>

        <AnimatePresence>
          {success && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}><Alert severity="success" sx={{ mb: 2 }}>{success}</Alert></motion.div>}
          {error && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert></motion.div>}
        </AnimatePresence>

        {/* Group Tree */}
        {groups.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Group sx={{ fontSize: 64, color: '#CBD5E1', mb: 2 }} />
            <Typography variant="h4" color="text.secondary" gutterBottom>No hay grupos</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Creá un grupo para organizar empleados y pacientes
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setCreateDialog({ open: true, parentId: null }); setFormName(''); setFormDesc(''); }}>
              Crear primer grupo
            </Button>
          </Paper>
        ) : (
          groups.map(g => renderGroup(g, 0))
        )}
      </Container>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialog.open || editDialog.open} onClose={() => { setCreateDialog({ open: false, parentId: null }); setEditDialog({ open: false, group: null }); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog.open ? 'Editar Grupo' : (createDialog.parentId ? 'Nuevo Sub-grupo' : 'Nuevo Grupo')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" value={formName} onChange={e => setFormName(e.target.value)} fullWidth autoFocus />
            <TextField label="Descripción" value={formDesc} onChange={e => setFormDesc(e.target.value)} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialog({ open: false, parentId: null }); setEditDialog({ open: false, group: null }); }}>Cancelar</Button>
          <Button variant="contained" onClick={editDialog.open ? handleUpdateGroup : handleCreateGroup} disabled={!formName.trim()}>
            {editDialog.open ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialog.open} onClose={() => setAddMemberDialog({ open: false, groupId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Miembro</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Rol en el grupo</InputLabel>
              <Select value={memberRole} label="Rol en el grupo" onChange={e => setMemberRole(e.target.value)}>
                <MenuItem value="member">Miembro</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Buscar empleado" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} fullWidth size="small" />
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {filteredEmployees.map(u => (
                <ListItem key={u.id} button onClick={() => handleAddMember(u.id)} sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: '#F0F9FF' } }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: u.role === 'admin' ? '#7C3AED' : '#2563EB' }}>
                      {(u.first_name?.[0] || '?').toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={`${u.first_name} ${u.last_name}`} secondary={`${u.email} — ${u.role}`} />
                </ListItem>
              ))}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialog({ open: false, groupId: null })}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Add Patient Dialog */}
      <Dialog open={addPatientDialog.open} onClose={() => setAddPatientDialog({ open: false, groupId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Paciente</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Buscar paciente" value={patientSearch} onChange={e => setPatientSearch(e.target.value)} fullWidth size="small" />
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {filteredPatients.map(u => (
                <ListItem key={u.id} button onClick={() => handleAddPatient(u.id)} sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: '#F0FFF4' } }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: '#10B981' }}>
                      {(u.first_name?.[0] || '?').toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={`${u.first_name} ${u.last_name}`} secondary={u.email} />
                </ListItem>
              ))}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPatientDialog({ open: false, groupId: null })}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, groupId: null, name: '' })}>
        <DialogTitle>¿Eliminar grupo?</DialogTitle>
        <DialogContent>
          <Typography>
            Se eliminará "{deleteConfirm.name}" y todos sus sub-grupos. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, groupId: null, name: '' })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteGroup}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <MobileBottomNav />
    </Box>
  );
}
